import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Container from 'react-bootstrap/Container';
import Card from 'react-bootstrap/Card';
import Button from 'react-bootstrap/Button';
import Alert from 'react-bootstrap/Alert';
import api from '../utils/api';
import { upsertCartItem, notifyCartUpdate } from '../utils/cartApi';
import './ProductDetail.css';

const CATEGORY_ITEMS = [
  'เตียงนอน',
  'โซฟา',
  'ชั้นวางทีวี',
  'โต๊ะเครื่องแป้ง',
  'ตู้เสื้อผ้า',
  'ฟูกนอน/ที่นอน',
  'ตู้โชว์',
  'หิ้งพระ',
  'ตู้กับข้าว',
  'ชุดเซ็ต'
];

const ALLOWED_TAGS = CATEGORY_ITEMS;

const toNumber = (value) => (value != null ? parseFloat(value) : null);

const hasRealPromo = (base, promo) => {
  const baseNum = toNumber(base);
  const promoNum = toNumber(promo);
  return baseNum != null && promoNum != null && promoNum < baseNum;
};

const getPricingOptions = (productData) => {
    if (!productData) {
      return [];
    }

    const options = [];

    const hasCashPromo = hasRealPromo(productData.price_cash, productData.price_cash_promo);
    const hasInstallmentPromo = hasRealPromo(
      productData.price_installment,
      productData.price_installment_promo
    );

    if (hasCashPromo) {
      options.push({
        key: 'cashPromo',
        type: 'cashPromo',
        label: 'ซื้อสด',
        note: 'โปรโมชัน',
        amount: productData.price_cash_promo,
        originalAmount: productData.price_cash
      });
    } else if (productData.price_cash != null) {
      options.push({
        key: 'cash',
        type: 'cash',
        label: 'ซื้อสด',
        note: null,
        amount: productData.price_cash,
        originalAmount: null
      });
    }

    if (hasInstallmentPromo) {
      options.push({
        key: 'installmentPromo',
        type: 'installmentPromo',
        label: 'ซื้อผ่อน',
        note: 'โปรโมชัน',
        amount: productData.price_installment_promo,
        originalAmount: productData.price_installment
      });
    } else if (productData.price_installment != null) {
      options.push({
        key: 'installment',
        type: 'installment',
        label: 'ซื้อผ่อน',
        note: null,
        amount: productData.price_installment,
        originalAmount: null
      });
    }

    return options.sort((a, b) => (a.amount || 0) - (b.amount || 0));
};

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [alert, setAlert] = useState({ show: false, message: '', variant: '' });
  const [isAdding, setIsAdding] = useState(false);
  const addStateTimeout = useRef(null);
  const wiggleTimeout = useRef(null);
  const [isButtonWiggling, setIsButtonWiggling] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(() => !!localStorage.getItem('token'));
  const [showLoginOverlay, setShowLoginOverlay] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  const getImageUrl = (imagePath) => {
    if (!imagePath) return 'https://via.placeholder.com/800x600';
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    return `/images/${imagePath}`;
  };

  useEffect(() => {
    const normalizeProductImages = (productData) => {
      if (!productData) {
        return [];
      }
      const source = Array.isArray(productData.product_images)
        ? productData.product_images
        : productData.product_image
          ? [productData.product_image]
          : [];
      return source
        .map((item) => (item == null ? '' : String(item).trim()))
        .filter(Boolean)
        .slice(0, 4);
    };

    const prepareProductData = (raw) => {
      if (!raw) {
        return null;
      }
      const images = normalizeProductImages(raw);
      return {
        ...raw,
        product_images: images,
        product_image: images[0] || raw.product_image || ''
      };
    };

    const initializePricing = (productData) => {
      const options = getPricingOptions(productData);
      setSelectedPricing(options[0] || null);
    };

    const fetchProduct = async () => {
      setIsLoading(true);
      try {
        const response = await api.get(`/api/products/${id}`);
        if (response.data) {
          const base = {
            ...response.data,
            tags: (Array.isArray(response.data.tags) ? response.data.tags : [])
              .filter((tag) => ALLOWED_TAGS.includes(tag))
              .filter((tag, index, self) => self.indexOf(tag) === index),
            highlights: Array.isArray(response.data.highlights)
              ? response.data.highlights
              : []
          };
          const normalizedProduct = prepareProductData(base);
          if (normalizedProduct) {
            setProduct(normalizedProduct);
            setActiveImageIndex(0);
            initializePricing(normalizedProduct);
          } else {
            setProduct(null);
          }
        } else {
          setProduct(null);
        }
      } catch (error) {
        console.error('Error fetching product:', error);
        setProduct(null);
      } finally {
        setIsLoading(false);
      }
    };
    fetchProduct();
  }, [id]);

  useEffect(() => {
    const syncAuthState = () => {
      setIsAuthenticated(!!localStorage.getItem('token'));
    };
    syncAuthState();
    window.addEventListener('storage', syncAuthState);
    return () => {
      window.removeEventListener('storage', syncAuthState);
    };
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      setShowLoginOverlay(false);
    }
  }, [isAuthenticated]);

  const [selectedPricing, setSelectedPricing] = useState(null);

  const galleryImages = useMemo(() => {
    if (!product) {
      return [];
    }
    const images = Array.isArray(product.product_images)
      ? product.product_images
        .map((img) => (img == null ? '' : String(img).trim()))
        .filter(Boolean)
      : [];
    if (images.length > 0) {
      return images.slice(0, 4);
    }
    return product.product_image ? [product.product_image] : [];
  }, [product]);

  useEffect(() => {
    if (!galleryImages || galleryImages.length === 0) {
      setActiveImageIndex(0);
      return;
    }
    if (activeImageIndex >= galleryImages.length) {
      setActiveImageIndex(0);
    }
  }, [galleryImages, activeImageIndex]);

  const activeImageSource = galleryImages[activeImageIndex] || galleryImages[0] || '';

  const formatPrice = (value) => {
    if (value == null) return '-';
    return `฿${parseFloat(value).toLocaleString()}`;
  };

  const addProductToCart = async ({ withFeedback = true } = {}) => {
    const token = localStorage.getItem('token');
    if (!token) {
      if (withFeedback) {
        setAlert({ show: false, message: '', variant: '' });
      }
      setShowLoginOverlay(true);
      if (wiggleTimeout.current) {
        clearTimeout(wiggleTimeout.current);
      }
      setIsButtonWiggling(true);
      wiggleTimeout.current = setTimeout(() => {
        setIsButtonWiggling(false);
      }, 800);
      return false;
    }

    const productId = product.product_id;
    const activePricing = selectedPricing || getPricingOptions(product)[0];

    if (!activePricing) {
      if (withFeedback) {
        setAlert({
          show: true,
          message: 'ไม่พบข้อมูลราคา กรุณาติดต่อผู้ดูแลระบบ',
          variant: 'danger'
        });
      }
      return false;
    }

    const { key, amount, label } = activePricing;
    const result = await upsertCartItem({
      productId,
      pricingType: key,
      quantity: 1,
      unitPrice: amount,
      pricingLabel: label,
      mode: 'increment'
    });

    if (result.unauthorized) {
      if (withFeedback) {
        setAlert({ show: false, message: '', variant: '' });
      }
      setShowLoginOverlay(true);
      return false;
    }

    if (result.error) {
      if (withFeedback) {
        setAlert({
          show: true,
          message: 'ไม่สามารถเพิ่มสินค้าในตะกร้าได้ กรุณาลองใหม่อีกครั้ง',
          variant: 'danger'
        });
      }
      return false;
    }

    notifyCartUpdate(result.items);
    if (withFeedback) {
      setAlert({
        show: true,
        message: 'เพิ่มสินค้าในลิสต์เรียบร้อยแล้ว',
        variant: 'success'
      });
      if (addStateTimeout.current) {
        clearTimeout(addStateTimeout.current);
      }
      setIsAdding(true);
      addStateTimeout.current = setTimeout(() => {
        setIsAdding(false);
      }, 1200);
    }
    return true;
  };

  const handleAddToCart = async () => {
    await addProductToCart({ withFeedback: true });
  };

  const handleBuyNow = async () => {
    const success = await addProductToCart({ withFeedback: false });
    if (success) {
      navigate('/cart');
    }
  };

  const handleCloseLoginOverlay = () => {
    setShowLoginOverlay(false);
  };

  useEffect(() => {
    return () => {
      if (addStateTimeout.current) {
        clearTimeout(addStateTimeout.current);
      }
      if (wiggleTimeout.current) {
        clearTimeout(wiggleTimeout.current);
      }
    };
  }, []);

  if (isLoading) {
    return (
      <div className="product-detail-page loading">
        <Container>
          <div className="loading-state">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="product-detail-page">
        <Container>
          <Alert variant="danger">ไม่พบสินค้า</Alert>
        </Container>
      </div>
    );
  }

  const descriptionLines = (product.product_description || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  const detailItems =
    Array.isArray(product.highlights) && product.highlights.length > 0
      ? product.highlights
      : descriptionLines;
  const displayTags = Array.isArray(product.tags)
    ? Array.from(new Set(product.tags.filter((tag) => ALLOWED_TAGS.includes(tag))))
    : [];
  const pricingOptions = getPricingOptions(product);

  return (
    <div className="product-detail-page">
      <Container>
        {alert.show && (
          <Alert variant={alert.variant} dismissible onClose={() => setAlert({ show: false })}>
            {alert.message}
          </Alert>
        )}

        {showLoginOverlay && (
          <div className={`login-overlay${showLoginOverlay ? ' visible' : ''}`} role="dialog" aria-modal="true">
            <div className="overlay-backdrop" onClick={handleCloseLoginOverlay}></div>
            <div className="overlay-card">
              <button type="button" className="overlay-close" onClick={handleCloseLoginOverlay} aria-label="ปิด">
                <i className="bi bi-x-lg"></i>
              </button>
              <div className="overlay-icon">
                <i className="bi bi-shield-lock"></i>
              </div>
              <h3>เข้าสู่ระบบเพื่อบันทึกลิสต์สินค้า</h3>
              <p>
                บัญชีสมาชิกช่วยให้คุณบันทึกรายการที่ชอบ บริหารคำสั่งซื้อได้สะดวก และติดตามโปรโมชั่นที่เหมาะกับคุณ
              </p>
              <div className="overlay-actions">
                <Button className="btn-primary-custom" size="lg" onClick={() => navigate('/login')}>
                  เข้าสู่ระบบ
                </Button>
                <Button variant="light" size="lg" onClick={() => navigate('/register')}>
                  สมัครสมาชิกใหม่
                </Button>
              </div>
            </div>
          </div>
        )}

        <div className="detail-wrapper">
          <div className="detail-gallery">
            <div className="gallery-main">
              <img src={getImageUrl(activeImageSource)} alt={product.product_name} />
            </div>
            {galleryImages.length > 1 && (
              <div className="gallery-thumbs">
                {galleryImages.map((image, index) => (
                  <button
                    key={`${image}-${index}`}
                    type="button"
                    className={`gallery-thumb${index === activeImageIndex ? ' active' : ''}`}
                    onClick={() => setActiveImageIndex(index)}
                    aria-label={`ดูภาพสินค้า ${index + 1}`}
                  >
                    <img src={getImageUrl(image)} alt={`${product.product_name} - ภาพที่ ${index + 1}`} />
                  </button>
                ))}
              </div>
            )}
            <div className="gallery-note">ภาพสินค้าใช้เพื่อการโฆษณา สีอาจแตกต่างจากสินค้าจริงเล็กน้อย</div>
          </div>

          <div className="detail-content">
            <h1>{product.product_name}</h1>
            <p className="detail-description">
              {descriptionLines.length > 0 ? descriptionLines[0] : 'สินค้าเฟอร์นิเจอร์คุณภาพจากแสงสว่างเฟอร์นิเจอร์'}
            </p>

            <div className="detail-price-box">
              <span className="price-label">เลือกรูปแบบการชำระเงิน</span>
              <div className="pricing-options">
                {pricingOptions.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    className={`pricing-option${selectedPricing?.key === option.key ? ' active' : ''}`}
                    onClick={() => setSelectedPricing(option)}
                  >
                    <div className="pricing-text">
                      <span className="pricing-label">{option.label}</span>
                      {option.note && <span className="pricing-note">{option.note}</span>}
                    </div>
                    <div className="pricing-value">
                      <span className="pricing-amount">{formatPrice(option.amount)}</span>
                      {option.originalAmount != null && (
                        <span className="pricing-original">{formatPrice(option.originalAmount)}</span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {displayTags.length > 0 && (
              <div className="detail-tags">
                {displayTags.map((tag) => (
                  <span key={tag} className="tag-badge">
                    #{tag}
                  </span>
                ))}
              </div>
            )}

            {detailItems && detailItems.length > 0 && (
              <div className="detail-list">
                <h3>รายละเอียดสินค้า</h3>
                <ul>
                  {detailItems.map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="detail-actions">
              <Button
                variant="light"
                className="btn-outline buy-now-btn"
                size="lg"
                onClick={handleBuyNow}
              >
                <i className="bi bi-bag-check me-2"></i>
                ซื้อสินค้า
              </Button>
              <Button
                className={`btn-primary-custom add-to-cart-btn${isAdding ? ' added' : ''}${!isAuthenticated ? ' needs-login' : ''}${isButtonWiggling ? ' wiggle' : ''}`}
                size="lg"
                onClick={handleAddToCart}
                disabled={isAdding}
              >
                <i className={`bi ${isAdding ? 'bi-check-circle' : 'bi-cart-plus'} me-2`}></i>
                {isAuthenticated ? (isAdding ? 'เพิ่มแล้ว!' : 'เพิ่มลงตะกร้า') : 'เข้าสู่ระบบก่อนเพิ่ม'}
              </Button>
            </div>

            <div className="detail-meta">
              <Card>
                <Card.Body>
                  <div>
                    <span>บริการจัดส่ง</span>
                    <strong>ฟรีในเขตกรุงเทพฯ และปริมณฑล</strong>
                  </div>
                  <div>
                    <span>การรับประกัน</span>
                    <strong>โครงสร้าง 1 ปี / ผ่อนสบายสูงสุด 12 งวด</strong>
                  </div>
                </Card.Body>
              </Card>
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
}

